import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileText, KeyRound, Laptop, PowerOff, Send } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { listAccounts } from '@/api/accounts';
import { listAssets, listIntuneOrphans } from '@/api/assets';
import { listContracts } from '@/api/contracts';

/**
 * Dashboard (PR-16) — assembles the triage / expiry widgets exported by the
 * feature PRs. The IT widgets are shown only to users who can read the IT
 * registers (canRead); others (e.g. an OnPass-only user) get a relevant panel
 * instead, so no widget mounts and no 403 fetch fires.
 */
export function Dashboard() {
  const { displayName, isAdmin, isHelpdesk, isFinance, isReviewer, canRead, canUseOnPass } = useAuth();

  const roleValue = isAdmin ? 'oscar-admin' : isHelpdesk ? 'oscar-helpdesk' : isFinance ? 'oscar-finance' : isReviewer ? 'oscar-reviewer' : 'no role assigned';

  return (
    <section className="max-w-4xl">
      <header className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-navy">Oscar</h1>
        <p className="mt-1 text-sm text-darkGrey">IT asset &amp; user management for Onsite Rental Group.</p>
      </header>

      {canRead ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Widget icon={KeyRound} label="Accounts needing triage" to="/accounts?needsTriage=1"
            queryKey={['dash', 'accounts-triage']} fetcher={async (s) => (await listAccounts({ needsTriage: true, pageSize: 1 }, s)).Total} />
          <OrphanWidget />
          <Widget icon={Laptop} label="Warranties expiring ≤90d" to="/assets"
            queryKey={['dash', 'warranty']} fetcher={async (s) => (await listAssets({ warrantyExpiringDays: 90, pageSize: 1 }, s)).Total} />
          <Widget icon={PowerOff} label="Inactive devices (>30d)" to="/assets?activity=inactive"
            queryKey={['dash', 'inactive']} fetcher={async (s) => (await listAssets({ activity: 'inactive', pageSize: 1 }, s)).Total} />
          <Widget icon={AlertTriangle} label="Primary-user mismatch" to="/assets?primaryUserMismatch=1"
            queryKey={['dash', 'mismatch']} fetcher={async (s) => (await listAssets({ primaryUserMismatch: true, pageSize: 1 }, s)).Total} />
          <Widget icon={AlertTriangle} label="Primary user not the assignee" to="/assets?primaryUserVsAssignee=1"
            queryKey={['dash', 'vs-assignee']} fetcher={async (s) => (await listAssets({ primaryUserVsAssignee: true, pageSize: 1 }, s)).Total} />
          <Widget icon={Laptop} label="Intune has a user, Oscar has none" to="/assets?intuneUserUnassigned=1"
            queryKey={['dash', 'intune-user-unassigned']} fetcher={async (s) => (await listAssets({ intuneUserUnassigned: true, pageSize: 1 }, s)).Total} />
          <Widget icon={FileText} label="Contracts expiring ≤90d" to="/contracts"
            queryKey={['dash', 'contracts']} fetcher={async (s) => (await listContracts({ expiringDays: 90 }, s)).length} />
        </div>
      ) : canUseOnPass ? (
        <Link to="/onpass" className="block max-w-md rounded border border-border bg-white p-5 shadow-sm transition-colors hover:border-orange">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy"><Send className="h-4 w-4" aria-hidden /> OnPass — share a secret</div>
          <p className="mt-2 text-sm text-darkGrey">Securely send a credential or message via a self-destructing, one-time link.</p>
        </Link>
      ) : (
        <div className="rounded border border-border bg-white p-5 shadow-sm">
          <p className="text-sm text-darkGrey">You don&rsquo;t have access to any Oscar features yet. Contact Onsite Support if you need access.</p>
        </div>
      )}

      <div className="mt-4 rounded border border-border bg-white p-5 shadow-sm">
        <p className="text-xs text-darkGrey/70">Signed in{displayName ? ` as ${displayName}` : ''} · role: <span className="font-mono">{roleValue}</span></p>
      </div>
    </section>
  );
}

/**
 * Intune orphans tile — shows how many still need triage (New, in orange) over
 * the total, e.g. "4 / 42", and deep-links to the New queue. Reuses the
 * ['dash','orphans'] key so the orphans page invalidation refreshes it.
 */
function OrphanWidget() {
  const query = useQuery({ queryKey: ['dash', 'orphans'], queryFn: ({ signal }) => listIntuneOrphans(signal) });
  const all = query.data ?? [];
  const needs = all.filter((o) => o.TriageStatus === 'New').length;
  const total = all.length;
  const ready = !query.isLoading && !query.isError;
  return (
    <Link to="/assets/intune-orphans?status=New" className="block rounded border border-border bg-white p-5 shadow-sm transition-colors hover:border-orange">
      <div className="flex items-center gap-2 text-sm font-semibold text-navy"><Laptop className="h-4 w-4" aria-hidden /> Intune orphans to triage</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-orange">{query.isLoading ? '…' : query.isError ? '—' : needs}</span>
        {ready && <span className="text-lg font-semibold text-darkGrey/50">/ {total}</span>}
      </div>
      {ready && <p className="mt-1 text-xs text-darkGrey/60">{needs} new · {total} total</p>}
    </Link>
  );
}

function Widget({ icon: Icon, label, to, queryKey, fetcher }: {
  icon: typeof KeyRound;
  label: string;
  to: string;
  queryKey: string[];
  fetcher: (signal?: AbortSignal) => Promise<number>;
}) {
  const query = useQuery({ queryKey, queryFn: ({ signal }) => fetcher(signal) });
  return (
    <Link to={to} className="block rounded border border-border bg-white p-5 shadow-sm transition-colors hover:border-orange">
      <div className="flex items-center gap-2 text-sm font-semibold text-navy"><Icon className="h-4 w-4" aria-hidden /> {label}</div>
      <div className="mt-2 text-3xl font-bold text-orange">{query.isLoading ? '…' : query.isError ? '—' : query.data}</div>
    </Link>
  );
}
